import { ErrorState, LoadingState } from '@/components/common';
import { ClientActivityTable } from '@/components/super-admin/dashboard/ClientActivityTable';
import { DashboardHeader } from '@/components/super-admin/dashboard/DashboardHeader';
import { GlobalProductSummary } from '@/components/super-admin/dashboard/GlobalProductSummary';
import { MetricsGrid } from '@/components/super-admin/dashboard/MetricsGrid';
import {
  ScheduledOperationsCard,
  CriticalAlertsCard,
} from '@/components/super-admin/dashboard/OperationalCards';
import { OrganizationAccountsTable } from '@/components/super-admin/dashboard/OrganizationAccountsTable';
import { Navbar } from '@/components/super-admin/general';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import { useSuperAdminDashboard } from '@/hooks/queries/useAnalytics';
import { useAuth } from '@/hooks/useAuth';
import { adaptSuperAdminMetrics, adaptClientActivityToTable } from '@/utils/dashboardAdapters';

export const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { data: dashboardData, isLoading, error, refetch } = useSuperAdminDashboard();
  
  // Adapt data for components
  const adaptedMetrics = dashboardData ? adaptSuperAdminMetrics(dashboardData.metrics) : {};
  const clientActivityTable = dashboardData
    ? adaptClientActivityToTable(dashboardData.clientActivity.clients)
    : { columns: [], data: [] };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Navbar
          navItems={superAdminNavItems}
          activeItem="Dashboard General"
          userName={user?.name || 'Super Admin'}
        />
        <main>
          <LoadingState message="Cargando dashboard..." />
        </main>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="min-h-screen">
        <Navbar
          navItems={superAdminNavItems}
          activeItem="Dashboard General"
          userName={user?.name || 'Super Admin'}
        />
        <main>
          <ErrorState
            title="Error al cargar el dashboard"
            message="No se pudo cargar la información del dashboard. Por favor, intenta nuevamente."
            error={error}
            showRetry
            onRetry={() => void refetch()}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Navbar */}
      <Navbar
        navItems={superAdminNavItems}
        activeItem="Dashboard General"
        userName={user?.name || 'Super Admin'}
      />

      <main>
        {/* Hero Section */}
        <DashboardHeader
          title="Panel Administrativo Finara"
          subtitle="Gestiona pagos, operaciones y clientes desde un solo lugar"
          showRealizarPago={true}
        />

        <div className="flex flex-col gap-[21px]">

          {/* Organization Accounts Table */}
          <OrganizationAccountsTable />

          {/* Metrics Grid */}
          <MetricsGrid metrics={adaptedMetrics} />

          {/* Client Activity Summary */}
          <ClientActivityTable
            columns={clientActivityTable.columns}
            data={clientActivityTable.data}
            title="Resumen de Actividad por Cliente"
          />

          {/* First Row of Operational Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[21px]">
            <ScheduledOperationsCard operations={dashboardData.scheduledOperations.operations} />
            <CriticalAlertsCard alerts={dashboardData.criticalAlerts.alerts} />
          </div>

          {/* Global Product Summary */}
          <GlobalProductSummary productSummary={dashboardData.productSummary} />
        </div>
      </main>
    </div>
  );
};
